import { NotificationAction } from "@config/local-notifications/notification-actions";
import { NotificationIcon } from "@config/local-notifications/notification-icon";
import {
  associationRepo,
  opportunityRepo,
  organizationManagerNotificationRepo,
} from "@repositories";
import { Roles, type BaseRoles } from "@shared/Roles";
import { copyApiPrefilterPerRole } from "@shared/backend/entity-security/copy-api-prefilter-per-role";
import { formatDate } from "@shared/backend/format-date";
import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { awaiter } from "@shared/helpers/awaiter";
import { textBuilder } from "@shared/helpers/text-builder";
import { Allow, remult } from "remult";
import { Association } from "../association";
import { Opportunity } from "../opportunity/entity";
import type { OrganizationManager } from "../organization-manager";
import { type OpportunityReport } from "./entity";

export const OpportunityReportEntityConfig: HyperEntityConfig<OpportunityReport> =
{
  id: {
    opportunityId: true,
    associationId: true,
  },
  allowApiCrud: Allow.authenticated,
  apiPrefilterPerRole: async (filter) => {
    filter(Roles.User, {
      $or: [
        {
          association: {
            $is: await copyApiPrefilterPerRole(Association),
          },
        },
        {
          opportunity: {
            $is: await copyApiPrefilterPerRole(Opportunity),
          },
        },
      ],
    });
    filter(Roles.OrganizationManagers, {
    });
  },
  allowInsertPerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );
  },

  allowUpdatePerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );
  },

  allowDeletePerRole(allow) {
    allow(Roles.User, (entity) =>
      remult.user!.associations!.includes(entity.associationId),
    );

    allow(Roles.OrganizationManagers, {
      opportunity: {
        $is: {
          organization: {
            $id: remult.user!.organizationId,
          },
        },
      },
    });
  },

  async onInsert(entity, e) {
    const [opportunity, association] = await awaiter(
      opportunityRepo.findId(entity.opportunityId, {
        include: {
          organization: {
            include: {
              managers: true,
            },
          },
        },
      }),
      associationRepo.findId(entity.associationId, {
        include: { user: true },
      }),
    );

    if (
      !opportunity ||
      !opportunity.organization ||
      !association ||
      !association.user
    )
      return;

    const managers = Array.from(
      new Set(
        [
          opportunity.manager,
          ...(opportunity.organization?.managers.filter((v) =>
            v.roles.some((role) => Roles.Admins.includes(role as BaseRoles)),
          ) ?? []),
        ].filter(Boolean) as OrganizationManager[],
      ),
    );

    const user = association.user;

    const text = textBuilder()
      .if(user.gender === "MALE", "המתנדב")
      .if(user.gender === "FEMALE", "המתנדבת")
      .if(user.gender === "OTHER", "המתנדב/ת")
      .space()
      .add(user.name)
      .space()
      .if(user.gender === "MALE", "הגיש")
      .if(user.gender === "FEMALE", "הגישה")
      .if(user.gender === "OTHER", "הגיש/ה")
      .space()
      .add("דיווח על ביצוע המשימה")
      .space()
      .add(opportunity.title)
      .space()
      .add("ב")
      .add(formatDate(entity.createdAt))
      .add(".")
      .done();

    await organizationManagerNotificationRepo.insert(
      managers.map((v) => ({
        toId: v.id,
        content: text,
        title: "דיווח ביצוע משימה",
        icon: NotificationIcon.FULL_CALENDAR,
        actions: [
          {
            id: NotificationAction.NAVIGATE,
            btnText: "לפרטים",
            extra: {
              to: "/events-reports",
            },
          },
        ],
      })),
    );
  },
};
